import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCompleteProject,
  saveCompleteProject,
  submitCompleteProject,
} from '../../../api/twoDays/board.api';
import { EMPTY_DRAFT, normalizeDraft } from './completeProject';

/** Complete project 초안 불러오기·자동 저장·제출 */

const SAVE_DELAY_MS = 800;

export const usePeerReviewDraft = (ctx, cp) => {
  const { live, slug, data, dispatch, now, refresh } = ctx;
  const [remote, setRemote] = useState({
    loading: !!live,
    draft: null,
    savedAt: null,
    failed: false,
  });
  const [saveError, setSaveError] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);
  const latest = useRef(null);

  useEffect(() => {
    if (!live || !slug) return undefined;
    let cancelled = false;
    getCompleteProject(slug)
      .then((r) => {
        if (cancelled) return;
        const draft = normalizeDraft(r?.draft);
        latest.current = draft;
        setRemote({ loading: false, draft, savedAt: r?.savedAt ?? null, failed: false });
      })
      .catch(
        () => !cancelled && setRemote({ loading: false, draft: null, savedAt: null, failed: true }),
      );
    return () => {
      cancelled = true;
    };
  }, [live, slug]);

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    const draft = pending.current;
    if (!live || !draft) return Promise.resolve();
    pending.current = null;
    return saveCompleteProject(slug, draft)
      .then((r) => {
        setSaveError(false);
        setRemote((s) => ({ ...s, savedAt: r?.savedAt ?? s.savedAt }));
      })
      .catch((e) => {
        setSaveError(true);
        throw e;
      });
  }, [live, slug]);

  useEffect(() => () => flush().catch(() => {}), [flush]);

  const mine = data.completeProject?.byMember?.[data.viewerId];
  const draft = live ? (remote.draft ?? EMPTY_DRAFT) : normalizeDraft(mine?.draft);
  const savedAt = live ? remote.savedAt : (mine?.savedAt ?? null);
  const editable = cp.kind === 'open' && !remote.loading && !remote.failed;

  const update = useCallback(
    (fn) => {
      if (!editable) return;
      if (!live) {
        dispatch({ type: 'cpSaveDraft', update: fn, at: now.toISOString() });
        return;
      }
      const next = fn(latest.current ?? EMPTY_DRAFT);
      latest.current = next;
      pending.current = next;
      setRemote((s) => ({ ...s, draft: next }));
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => flush().catch(() => {}), SAVE_DELAY_MS);
    },
    [editable, live, dispatch, now, flush],
  );

  const submit = useCallback(async () => {
    if (!live) {
      // 제출 시각은 실제 시각 — 목 보드의 고정 시각(now)을 쓰지 않는다
      dispatch({ type: 'cpSubmit', at: new Date().toISOString(), late: cp.step === 1 });
      return;
    }
    pending.current = null;
    window.clearTimeout(timer.current);
    await submitCompleteProject(slug, latest.current ?? draft);
    await refresh?.();
  }, [live, dispatch, now, cp.step, slug, draft, refresh]);

  return {
    draft,
    savedAt,
    editable,
    loading: remote.loading,
    loadFailed: remote.failed,
    saveError,
    update,
    submit,
  };
};
